import clsx from "clsx";
import { Crosshair, MapPinOff, RotateCcw, Undo2, UserMinus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { onExit } from "../api/exitFlush";
import type { DeployedUnit, Phase } from "../api/types";
import { CardSection, PropRow } from "../components/panel";
import { SaveDossierHint } from "../components/SaveDossierHint";
import { Badge, Button, FloatingPanel, IconButton } from "../components/ui";
import { echelonName, echelonNameFromSidc, taskOrgFromSidc } from "../echelon";
import { unitDesignation } from "../i18n/designation";
import { k, locale, t } from "../i18n/i18n";
import { term, unitMissionName } from "../i18n/vocabulary";
import { formatMgrs } from "../map/MapCanvas";
import { symbolSvg } from "../map/units/symbols";
import { potentialLabel, type Tone } from "../map/units/unitInfo";
import { useDeployment } from "../map/units/useDeployment";
import { useCompositionWrites } from "../map/units/useCompositionWrites";
import { roleName, useUnitMoves } from "../map/units/useUnitMoves";
import { defensiveRoles, roleLong, whyOf } from "../map/units/propagation";
import { useManoeuvreWrites } from "../map/manoeuvre/useManoeuvreWrites";
import { specialtyLabel } from "../missions";
import { MissionChevron } from "./MissionMenu";
import { sameMission, standingTaskLabel } from "./missionMenuModel";
import { useUi } from "../state/ui";
import { functionLabel } from "../views/orbats/app6";
import { RoleSelect } from "../views/composition/RoleSelect";
import { canChooseRole, canRemove, offTemplate, sideView, unitByKey } from "../views/composition/rows";
import { useApp6Palette } from "../views/orbats/useApp6Palette";
import { CardActions, CardFooter, CardTitle } from "./cardParts";
import { refuseIfDossierLocked } from "../sharing/access";
import { UnitCapops } from "./UnitCapops";

/** Fiche d'unité (panneau droit) : identité APP-6, mission et lieu à chaque temps, moyens. */

const PHASES: Phase[] = ["T1", "T2", "T3"];

const SIDE: Record<
  DeployedUnit["side"],
  { label: string; tone: "friend" | "hostile" | "ok" }
> = {
  AMI: { label: k("Ami"), tone: "friend" },
  ENI: { label: k("Ennemi"), tone: "hostile" },
  NEUTRE: { label: k("Neutre"), tone: "ok" },
};

/** GTIA de brigade placés en arrière du dispositif, hors des rôles tactiques de la mêlée. */
const SUPPORT_LABEL: Record<string, string> = {
  artillerie: k("appui feux"),
  génie: k("appui génie"),
  soutien: k("soutien"),
  réserve: k("réserve"),
};

const PLAYED_HINT = k("Modes d'action ennemis : §1.a de l'OPO ; ligne FORAD de la matrice du §3.b");

const ARM: Record<string, string> = {
  INFANTERIE: k("Infanterie"),
  INFANTERIE_MECA: k("Infanterie mécanisée"),
  INFANTERIE_MOTO: k("Infanterie motorisée"),
  BLINDE: k("Arme blindée"),
  CAVALERIE: k("Cavalerie (reconnaissance blindée)"),
  RECONNAISSANCE: k("Reconnaissance"),
  INTERARMES: k("Interarmes"),
  ARTILLERIE: k("Artillerie"),
  LANCE_ROQUETTES: k("Lance-roquettes multiples"),
  DEFENSE_SOL_AIR: k("Défense sol-air"),
  MORTIER: k("Mortiers"),
  APPUI_FEUX: k("Coordination des feux"),
  GENIE: k("Génie"),
  DEMINAGE: k("Déminage"),
  NRBC: k("NRBC"),
  TRAIN: k("Train"),
  LOGISTIQUE: k("Logistique"),
  RAVITAILLEMENT: k("Ravitaillement"),
  MAINTENANCE: k("Maintenance"),
  SANTE: k("Santé"),
  COMMANDEMENT: k("Commandement"),
  TRANSMISSIONS: k("Transmissions"),
  GUERRE_ELECTRONIQUE: k("Guerre électronique"),
  RENSEIGNEMENT: k("Renseignement"),
  INFLUENCE: k("Influence"),
  DRONES: k("Drones"),
  ROBOTS: k("Robots"),
  IRREGULIERS: k("Groupe armé irrégulier"),
  POPULATION: k("Population civile"),
};

export function UnitCard({ code }: { code: string }) {
  const { data } = useDeployment();
  const phase = useUi((s) => s.phase);
  const exerciseId = useUi((s) => s.exerciseId);
  const setPhase = useUi((s) => s.setPhase);
  const select = useUi((s) => s.select);
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  const { canMove, reset, busy } = useUnitMoves();
  const writes = useCompositionWrites();
  const missionWrites = useManoeuvreWrites();
  const requestSave = useUi((s) => s.requestSave);
  // Commande d'écriture bloquée : lecture seule du dossier (le bandeau), sinon dossier à enregistrer.
  const blockedWrite = () => {
    if (!refuseIfDossierLocked()) requestSave();
  };
  const unit = data?.units.find((u) => u.code === code);
  const palette = useApp6Palette(Boolean(unit?.function || unit?.app6));
  if (!unit) return null;
  // Unité de la composition du dossier (docs/18 §6.3 ; ordre de bataille ennemi, docs/14 §3.4) : son rôle se choisit
  // ici, pour ce dossier, et elle s'en retire.
  const side = unit.side === "AMI" || unit.side === "ENI" ? sideView(data, unit.side) : null;
  const member = unitByKey(side, unit.key);
  const roleChoice = member && canChooseRole(side, member) ? member : undefined;
  const removableFromDossier = member && canRemove(side, member) ? member : undefined;
  const offMap = member && offTemplate(member) ? member : undefined;
  const rotation = data?.centac ?? null;
  const defensive = defensiveRoles(data);

  const pos = unit.positions[phase];
  const support = unit.support ? SUPPORT_LABEL[unit.support] : undefined;
  const roleLabel = unit.support
    ? support && t(support)
    : unit.role
      ? roleName(unit.role, defensive)
      : undefined;
  // Rôle tactique lu en profondeur (docs/25 §4) : repère commun à la carte et à l'avis de propagation ; rôles d'une
  // défense (registre A126).
  const tactical = roleLong(unit.tactical_role, unit.side, defensive);
  const arm = ARM[unit.arm];
  const friendly = unit.side === "AMI";
  // Unité d'un ordre de bataille construit : fonction et échelon APP-6 choisis dans l'éditeur (symbole), le
  // catalogue garde ses libellés.
  const built = Boolean(unit.function || unit.app6);
  const builtFunction = built ? functionLabel(palette, unit.function, unit.app6) : null;
  const armLabel = builtFunction ?? (arm ? t(arm) : undefined);
  // Rotation CENTAC : la cellule FORAD joue les modes d'action ennemis ; seul l'appui a une mission fixe.
  // L'ennemi est la FORAD seule : aucune unité d'échelon supérieur ennemi sur la carte (29/10/2026).
  const played = Boolean(rotation) && unit.side === "ENI";
  // PC, trains de combat (A95) : même tâche à chaque temps, dite une fois ; chaque temps ne montre que le lieu.
  const standing = standingTaskLabel(unit);
  const builtEchelon = built ? echelonNameFromSidc(unit.sidc, unit.arm, friendly) : undefined;
  const builtTaskOrg = built ? taskOrgFromSidc(unit.sidc) : null;
  const echelon = built
    ? builtTaskOrg
      ? `${term(builtTaskOrg)} (${builtEchelon?.toLowerCase() ?? "—"})`
      : (builtEchelon ?? "—")
    : unit.task_org
      ? `${term(unit.task_org === "SGTIA" ? "S/GTIA" : unit.task_org)} (${echelonName(unit.echelon, unit.arm, friendly)?.toLowerCase() ?? "—"})`
      : (echelonName(unit.echelon, unit.arm, friendly) ?? "—");
  const goTo = (p: Phase) => {
    const target = unit.positions[p];
    setPhase(p);
    if (target) {
      select({
        layer: "unit",
        code: unit.code,
        lngLat: [target.lon, target.lat],
      });
      requestFlyTo([target.lon, target.lat], 12.5);
    }
  };

  const subtitle =
    [unit.display_name !== unit.designation ? unit.display_name : null, unit.parent]
      .filter(Boolean)
      .map(unitDesignation)
      .join(" · ") || armLabel;

  return (
    <FloatingPanel
      enter="right"
      width="card"
      tallHeader
      icon={
        // Symbole APP-6 : son code SIDC est dans la bulle (plus de ligne « SIDC » dans la fiche).
        <span
          role="img"
          aria-label={t("Symbole APP-6 de {unit}", { unit: unitDesignation(unit.designation) })}
          title={t("Code SIDC APP-6D : {sidc}", { sidc: unit.sidc })}
          className="flex w-9 justify-center"
          dangerouslySetInnerHTML={{ __html: symbolSvg(unit.sidc, 26) }}
        />
      }
      title={<CardTitle title={unitDesignation(unit.designation)} subtitle={subtitle} />}
      actions={<CardActions />}
      footer={
        pos ? (
          <CardFooter>
            <Button variant="secondary" size="sm" onClick={() => requestFlyTo([pos.lon, pos.lat], 13)}>
              <Crosshair size={14} /> {t("Centrer")}
            </Button>
            {offMap && (
              <Button
                variant="secondary"
                size="sm"
                disabled={writes.busy}
                blocked={!writes.canEdit}
                onBlocked={blockedWrite}
                onClick={() => {
                  select(null);
                  writes.unplace.mutate(offMap.key);
                }}
              >
                <MapPinOff size={14} /> {t("Retirer de la carte")}
              </Button>
            )}
            {pos.moved && !offMap && (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                blocked={!canMove}
                onBlocked={blockedWrite}
                onClick={() => reset.mutate({ unit: unit.key, phase })}
              >
                <RotateCcw size={14} /> {t("Revenir à la position doctrinale")}
              </Button>
            )}
            {removableFromDossier && (
              <Button
                variant="danger-ghost"
                size="sm"
                disabled={writes.busy}
                blocked={!writes.canEdit}
                onBlocked={blockedWrite}
                title={t("Dans ce dossier seulement : le catalogue et l'ordre de bataille restent inchangés.")}
                onClick={() => {
                  select(null);
                  writes.compose.mutate({ op: "remove", side: removableFromDossier.side, key: removableFromDossier.id });
                }}
              >
                <UserMinus size={14} /> {t("Retirer du dossier")}
              </Button>
            )}
          </CardFooter>
        ) : undefined
      }
    >
      <div className="flex flex-wrap gap-1.5 px-4 pt-3 pb-2">
        <Badge tone={SIDE[unit.side].tone}>{t(SIDE[unit.side].label)}</Badge>
        {unit.effort.map((p) => (
          <Badge key={p} dot tone="accent">
            {t("Effort principal en {phase}", { phase: p })}
          </Badge>
        ))}
        {tactical ? (
          <Badge tone={unit.side === "ENI" ? "hostile" : "friend"} title={t("Rôle tactique lu sur la carte, d'après la profondeur le long de l'axe")}>
            {tactical.charAt(0).toUpperCase() + tactical.slice(1)}
          </Badge>
        ) : (
          roleLabel && unit.side === "AMI" && <Badge>{roleLabel.charAt(0).toUpperCase() + roleLabel.slice(1)}</Badge>
        )}
        {unit.role === "eniSupport" && <Badge>{t("Appui — 2e échelon")}</Badge>}
        {unit.hidden && (
          <Badge dot tone="warn">
            {t("Unité non joueuse — sans mission, non dessinée")}
          </Badge>
        )}
        {unit.inferred && (
          <Badge dot tone="warn">
            {t("typée d'après son nom")}
          </Badge>
        )}
      </div>

      <div className="px-4 pb-2">
        <PropRow label={t("Échelon")}>{echelon}</PropRow>
        <PropRow label={t("Fonction")}>{armLabel ?? unit.arm}</PropRow>
        {unit.specialty && arm !== unit.specialty && (
          <PropRow label={t("Spécialité")}>{specialtyLabel(unit.specialty)}</PropRow>
        )}
        {roleChoice && (
          <PropRow label={t("Rôle")} className="items-start">
            <span className="inline-block w-[230px] max-w-full text-left">
              <RoleSelect
                unit={roleChoice}
                disabled={writes.busy}
                onChange={(role) => {
                  if (!writes.canEdit) return blockedWrite();
                  writes.setRole.mutate({ side: roleChoice.side, unit: roleChoice.id, role });
                }}
              />
            </span>
          </PropRow>
        )}
      </div>

      <CardSection
        title={
          standing
            ? t("Lieu par temps")
            : rotation
              ? t("Missions — OPO {n}", { n: rotation.opo })
              : t("Mission par temps")
        }
      >
        {standing && <p className="px-2 pb-1.5 text-meta leading-snug text-ink-muted">{standing}</p>}
        <div className="space-y-1">
          {PHASES.map((p) => {
            const at = unit.positions[p];
            const step = rotation?.phases[p];
            const doctrinal =
              at?.overridden && at.doctrinal_mission !== null && !sameMission(at.doctrinal_mission, at.mission)
                ? at.doctrinal_mission
                : null;
            // Deux boutons frères (docs/14 §2.1) : « aller au temps » et le chevron du menu de mission.
            return (
              <div
                key={p}
                className={clsx(
                  "flex min-h-8 items-center gap-1 rounded-lg py-1.5 pr-1 pl-2 transition-colors",
                  p === phase ? "bg-selected" : at && "hover:bg-hover",
                )}
              >
                <button
                  type="button"
                  onClick={() => goTo(p)}
                  disabled={!at}
                  title={at ? t("Afficher {phase} et localiser l'unité", { phase: p }) : undefined}
                  className="focus-inset flex min-w-0 flex-1 items-start gap-2.5 text-left disabled:opacity-40"
                >
                  <span
                    className={clsx(
                      "mt-px w-6 shrink-0 text-ui font-semibold tabular-nums",
                      p === phase ? "text-ink" : "text-ink-muted",
                    )}
                  >
                    {p}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-ui font-semibold text-ink">
                      {standing ? (
                        <span>{at ? at.locality || t("Lieu-dit sans nom") : t("Absente")}</span>
                      ) : (
                      <span
                        title={
                          at?.mission_label
                            ? at.mission
                            : played
                              ? t(PLAYED_HINT)
                              : undefined
                        }
                      >
                        {at
                          ? unitMissionName(at.mission_label || at.mission) ||
                            (played
                              ? t("Action jouée par la FORAD")
                              : t("Sans mission"))
                          : t("Absente")}
                      </span>
                      )}
                      {at?.overridden && (
                        <Badge dot size="sm" tone="accent" title={t("Mission choisie à la main : la propagation ne la change jamais")}>
                          {t("modifiée")}
                        </Badge>
                      )}
                      {at && !at.overridden && at.reason && (
                        <Badge dot size="sm" tone="neutral" title={whyOf(at.reason) ?? undefined}>
                          {t("adaptée à la posture")}
                        </Badge>
                      )}
                      {step && !step.in_rotation && unit.side === "AMI" && (
                        <Badge dot size="sm" tone="neutral" title={t("Au-delà des phases de l'OPO")}>
                          {t("Hors rotation")}
                        </Badge>
                      )}
                      {at?.moved && (
                        <Badge dot size="sm" tone="warn" title={t("Position déplacée à la main")}>
                          {t("déplacée")}
                        </Badge>
                      )}
                    </span>
                    {doctrinal !== null && (
                      <span className="block truncate text-meta font-normal text-ink-muted">
                        {t("doctrinale : {mission}", { mission: doctrinal ? unitMissionName(doctrinal) : t("Sans mission") })}
                      </span>
                    )}
                    {at && !at.overridden && at.reason && whyOf(at.reason) && (
                      <span className="block text-meta leading-snug font-normal text-ink-muted">{whyOf(at.reason)}</span>
                    )}
                    {at && (
                      <span className="block truncate text-meta font-normal text-ink-muted">
                        {standing ? "" : `${at.locality || t("Lieu-dit sans nom")} · `}
                        {formatMgrs(at.lon, at.lat, 3)}
                      </span>
                    )}
                  </span>
                </button>
                {at?.overridden && unit.mission_editable && !rotation && (
                  <IconButton
                    size="sm"
                    className="self-start"
                    disabled={missionWrites.busy}
                    onClick={() => {
                      if (refuseIfDossierLocked()) return;
                      missionWrites.setMission.mutate({ unit: unit.key, phase: p, mission: null, reposition: false });
                    }}
                    title={t("Revenir à la mission doctrinale ({mission})", {
                      mission: at.doctrinal_mission ? unitMissionName(at.doctrinal_mission) : t("Sans mission"),
                    })}
                    aria-label={t("Revenir à la mission doctrinale en {phase}", { phase: p })}
                  >
                    <Undo2 size={14} />
                  </IconButton>
                )}
                <MissionChevron unit={unit} phase={p} className="self-start" />
              </div>
            );
          })}
        </div>
      </CardSection>

      {(unit.materiel.length > 0 || unit.quantity) && (
        <CardSection title={t("Moyens")}>
          {unit.materiel.map((m) => (
            <div
              key={m.name}
              className="border-b border-line py-1.5 last:border-b-0"
            >
              <div className="text-ui font-semibold text-ink">{m.name}</div>
              {m.known ? (
                <div className="text-meta leading-snug text-ink-muted">
                  {m.category}
                  {m.speed_kmh ? ` · ${m.speed_kmh} km/h` : ""}
                  {m.range_m
                    ? ` · ${t("portée {range} m", { range: m.range_m.toLocaleString(locale()) })}`
                    : ""}
                </div>
              ) : (
                <div className="text-meta text-ink-muted">
                  {t("Hors du catalogue des matériels majeurs")}
                </div>
              )}
            </div>
          ))}
          {unit.quantity ? (
            <PropRow label={t("Quantité nominale")}>{unit.quantity}</PropRow>
          ) : null}
        </CardSection>
      )}

      <PotentialSection
        key={unit.key}
        unit={unit}
        strength={data?.placement?.strengths?.[unit.key]}
      />

      {/* CAPOPS (chantier « DTO / CAPOPS ») : unité amie d'un dossier enregistré, hors CENTAC (rien à afficher). */}
      {exerciseId && friendly && !rotation && <UnitCapops exerciseId={exerciseId} unitKey={unit.key} phase={phase} />}

      <CardSection title={t("Position en {phase}", { phase })}>
        {pos ? (
          <>
            <PropRow label="MGRS" mono>
              {formatMgrs(pos.lon, pos.lat)}
            </PropRow>
            <PropRow label={t("Lat / Lon")} mono>
              {pos.lat.toFixed(5)} / {pos.lon.toFixed(5)}
            </PropRow>
            {!canMove && <SaveDossierHint action="déplacer l'unité" className="pt-1.5" />}
            {canMove && !pos.moved && (
              <p className="pt-1.5 text-meta leading-snug text-ink-muted">
                {rotation && unit.side === "AMI"
                  ? t("Faites glisser le symbole sur la carte pour déplacer l'unité pour tout l'OPO {n} (T1 à T3).", { n: rotation.opo })
                  : rotation
                    ? t("Faites glisser le symbole sur la carte pour déplacer l'unité en {phase}, dans tous les OPO.", { phase })
                    : t("Faites glisser le symbole sur la carte pour déplacer l'unité en {phase}.", { phase })}
              </p>
            )}
          </>
        ) : (
          <p className="text-ui text-ink-muted">{t("Unité absente de la carte à ce temps.")}</p>
        )}
      </CardSection>
    </FloatingPanel>
  );
}

const TONE_TEXT: Record<Tone, string> = { ok: "text-ok", warn: "text-warn", brand: "text-danger" };

/** Potentiel de l'unité (curseur POTENTIEL de l'iPad) : 0 à 110 % par pas de 10 %, « Renforcé » au-delà de
 *  100 %. Quantité engagée = quantité nominale × potentiel, reprise dans la bulle au survol ; sans effet sur
 *  l'ordre. Enregistré dans le dossier 0,35 s après le dernier cran, une requête à la fois. */
function PotentialSection({ unit, strength }: { unit: DeployedUnit; strength: number | undefined }) {
  const { canMove, setStrength } = useUnitMoves();
  const saved = Math.round((strength ?? 1) * 100);
  const [value, setValue] = useState(saved);
  const pending = useRef<number | null>(null);
  const inFlight = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const savedRef = useRef(saved);
  savedRef.current = saved;

  // Valeur du dossier changée (enregistrement, annulation) : le curseur la reprend, sauf pendant un réglage.
  useEffect(() => {
    if (pending.current === null) setValue(saved);
  }, [saved]);
  // Réglage en attente de sa pause (350 ms) : envoyé aussitôt si la fiche se ferme, si la page passe en arrière-plan
  // ou se ferme (requête non interruptible), jamais perdu (audit de persistance m11).
  const commitRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    const now = () => {
      window.clearTimeout(timer.current);
      if (pending.current !== null && !inFlight.current) commitRef.current();
    };
    const unsubscribe = onExit(now);
    return () => {
      unsubscribe();
      now();
    };
  }, []);

  const commit = () => {
    const v = pending.current;
    if (v === null) return;
    if (inFlight.current) {
      timer.current = window.setTimeout(commit, 150);
      return;
    }
    inFlight.current = true;
    setStrength.mutate(
      { unit: unit.key, value: v === 100 ? null : v / 100 },
      {
        onSettled: (_result, error) => {
          inFlight.current = false;
          if (pending.current !== v) return;
          pending.current = null;
          if (error) setValue(savedRef.current);
        },
      },
    );
  };

  commitRef.current = commit;

  const change = (v: number) => {
    setValue(v);
    pending.current = v;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(commit, 350);
  };

  const label = potentialLabel(value / 100);
  const engaged = unit.quantity ? Math.round(unit.quantity * (value / 100)) : null;
  return (
    <CardSection title={t("Potentiel")}>
      <div className="flex items-center gap-3">
        <span className="w-7 shrink-0 text-meta text-ink-muted tabular-nums">0 %</span>
        <input
          type="range"
          min={0}
          max={110}
          step={10}
          value={value}
          disabled={!canMove}
          onChange={(e) => change(Number(e.target.value))}
          aria-label={t("Potentiel de l'unité")}
          aria-valuetext={label.text}
          className="h-1 min-w-0 flex-1 cursor-pointer accent-accent disabled:cursor-not-allowed disabled:opacity-40"
        />
        <span className={clsx("w-16 shrink-0 text-right text-ui font-semibold tabular-nums", TONE_TEXT[label.tone])}>
          {label.text}
        </span>
      </div>
      <p className="pt-1.5 text-meta leading-snug text-ink-muted">
        {engaged !== null && unit.quantity
          ? `${t("Quantité engagée : {engaged} sur {total}.", { engaged, total: unit.quantity })} `
          : ""}
        {t("Reprise dans la bulle au survol de l'unité ; entre dans sa CAPOPS et pèse dans le rapport de forces.")}
      </p>
      {!canMove && <SaveDossierHint action="régler le potentiel" className="pt-1.5" />}
    </CardSection>
  );
}
